import React, { useState, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { Box, Typography, Paper, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { Product } from "../../../../interfaces/DTO/mislaka_xml_models";
import { formatCurrency, formatPercentage } from "../../../../utils/namesFormater";
import { InsuranceComponent } from "../InsuranceComponent";
import { formatInsurancePaymentFrequency, formatInsurancePlanType, formatYesNo } from "../../../../utils/pensionEnumFormatter";

interface BasicInsuranceDetailsCardProps {
  product: Product;
}

const BasicInsuranceDetailsCard: React.FC<BasicInsuranceDetailsCardProps> = ({ product }) => {
    const policy = product.policies_or_accounts[0]; // לוקחים את הפוליסה הראשונה
    const [selectedInsuredId, setSelectedInsuredId] = useState<string | null>(null);

    // זיהוי מבוטח ראשי ומבוטחים אחרים
    const insuredTypes = useMemo(() => {
      const clientId = product.client?.client_id;
      const otherInsured = new Map<string, string>();

      policy.insurence_coverages?.forEach((coverage) => {
        coverage.insured_details?.forEach((detail) => {
          if (detail.id_number && detail.id_number !== clientId) {
            otherInsured.set(detail.id_number, `מבוטח ${detail.id_number}`);
          }
        });
      });

      return {
        primaryInsuredId: clientId,
        otherInsured: Array.from(otherInsured.entries()).map(([id, name]) => ({ id, name }))
      };
    }, [policy.insurence_coverages, product.client?.client_id]);

    // סינון כיסויים לפי מבוטח נבחר
    const filteredCoverages = useMemo(() => {
      if (!selectedInsuredId) {
        // אם לא נבחר מבוטח ספציפי, הצג את כל כיסויי המבוטח הראשי
        return policy.insurence_coverages?.filter(coverage => {
          if (!coverage.insured_details || coverage.insured_details.length === 0) return true;
          return coverage.insured_details.some(detail => detail.id_number === insuredTypes.primaryInsuredId);
        }) || [];
      } else {
        // הצג כיסויים של המבוטח הנבחר
        return policy.insurence_coverages?.filter(coverage => {
          return coverage.insured_details?.some(detail => detail.id_number === selectedInsuredId);
        }) || [];
      }
    }, [policy.insurence_coverages, selectedInsuredId, insuredTypes.primaryInsuredId]);

    // חישוב מספר כיסויים לכל מבוטח (רק כיסויים עם basic_insurance)
    const primaryInsuredCoverageCount = useMemo(() => {
      return policy.insurence_coverages?.filter(coverage => {
        if (!coverage.basic_insurance || coverage.basic_insurance.length === 0) return false;
        if (!coverage.insured_details || coverage.insured_details.length === 0) return true;
        return coverage.insured_details.some(detail => detail.id_number === insuredTypes.primaryInsuredId);
      }).length || 0;
    }, [policy.insurence_coverages, insuredTypes.primaryInsuredId]);

    const otherInsuredCoverageCounts = useMemo(() => {
      const counts = new Map<string, number>();
      insuredTypes.otherInsured.forEach(({ id }) => {
        const count = policy.insurence_coverages?.filter(coverage => {
          if (!coverage.basic_insurance || coverage.basic_insurance.length === 0) return false;
          return coverage.insured_details?.some(detail => detail.id_number === id);
        }).length || 0;
        counts.set(id, count);
      });
      return counts;
    }, [policy.insurence_coverages, insuredTypes.otherInsured]);

    // חישוב סכום ביטוח כולל לפי מבוטח נבחר
    const totalInsuranceAmount = useMemo(() => {
      let total = 0;
      filteredCoverages.forEach(coverage => {
        coverage.basic_insurance?.forEach(insurance => {
          total += insurance.requested_insurance_amount || 0;
        });
      });
      return total;
    }, [filteredCoverages]);

    if (!policy.insurence_coverages || policy.insurence_coverages.length === 0) {
      return <Typography color="error">לא נמצאו כיסויי ביטוח עבור הפוליסה</Typography>;
    }

    const handleInsuredChange = (_event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
      setSelectedInsuredId(newValue);
    };

    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            פרטי ביטוח בסיסיים - {formatCurrency(totalInsuranceAmount)}
          </Typography>

          {/* כפתורי בחירת מבוטח - יוצגו רק אם יש מבוטח אחר */}
          {insuredTypes.otherInsured.length > 0 && (
            <ToggleButtonGroup
              value={selectedInsuredId}
              exclusive
              onChange={handleInsuredChange}
              size="small"
              sx={{ direction: 'rtl' }}
            >
              <ToggleButton value={null}>
                מבוטח ראשי ({primaryInsuredCoverageCount})
              </ToggleButton>
              {insuredTypes.otherInsured.map(({ id, name }) => (
                <ToggleButton key={id} value={id}>
                  {name} ({otherInsuredCoverageCounts.get(id) || 0})
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Box>

        {filteredCoverages.map((coverage, coverageIndex) =>
          coverage.basic_insurance?.map((insurance, insuranceIndex) => (
            <Paper
              key={`${coverageIndex}-${insuranceIndex}`}
              elevation={2}
              sx={{ p: 2, backgroundColor: "#f5f5f5", mb: 2 }}
            >
              {/* הצגת InsuranceComponent לפני הנתונים */}
              <InsuranceComponent insuranceDetails={insurance} />

              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                כיסוי ביטוחי {coverageIndex + 1}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">קוד מוצר:</Typography></Grid>
                <Grid size={{ xs: 6, md: 8 }}><Typography>{insurance.product_code || "-"}</Typography></Grid>

                <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">סכום ביטוח למוות:</Typography></Grid>
                <Grid size={{ xs: 6, md: 8 }}><Typography>{formatCurrency(insurance.insurance_amount) || "-"}</Typography></Grid>

                <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">סוג הצמדה סכום ביטוח:</Typography></Grid>
                <Grid size={{ xs: 6, md: 8 }}><Typography>{formatInsurancePaymentFrequency(insurance.adjustment_type) || "-"}</Typography></Grid>

                <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">סוג מסלול לביטוח:</Typography></Grid>
                <Grid size={{ xs: 6, md: 8 }}><Typography>{formatInsurancePlanType(insurance.insurance_plan_type) || "-"}</Typography></Grid>

                <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">האם כולל חיסכון:</Typography></Grid>
                <Grid size={{ xs: 6, md: 8 }}><Typography>{formatYesNo(insurance.includes_savings) || "-"}</Typography></Grid>

                <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">סכום ביטוח למסלול:</Typography></Grid>
                <Grid size={{ xs: 6, md: 8 }}><Typography>{formatCurrency(insurance.requested_insurance_amount) || "-"}</Typography></Grid>

                <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">מספר משכורות:</Typography></Grid>
                <Grid size={{ xs: 6, md: 8 }}><Typography>{insurance.salary_multiples || "-"}</Typography></Grid>

                <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">אחוז הקצאה לחיסכון:</Typography></Grid>
                <Grid size={{ xs: 6, md: 8 }}><Typography>{insurance.savings_allocation ? `${formatPercentage(insurance.savings_allocation)}` : "-"}</Typography></Grid>

                <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">תקרת גג חתם למקרה מוות:</Typography></Grid>
                <Grid size={{ xs: 6, md: 8 }}><Typography>{insurance.death_cap_ceiling || "-"}</Typography></Grid>

                <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">תקרת גג חתם לאובדן כושר עבודה:</Typography></Grid>
                <Grid size={{ xs: 6, md: 8 }}><Typography>{insurance.disability_cap_ceiling || "-"}</Typography></Grid>

                <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">אחוז הגדלת גג חתם:</Typography></Grid>
                <Grid size={{ xs: 6, md: 8 }}><Typography>{insurance.cap_increase_percentage ? `${formatPercentage(insurance.cap_increase_percentage)}` : "-"}</Typography></Grid>
              </Grid>
            </Paper>
          ))
        )}
      </Box>
    );
  };
  
  export default BasicInsuranceDetailsCard;
